import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { can, type Me } from '../auth/me';
import { warsawParts } from '../events/labels';

export const DOWNLOADS_RESOURCE = 'backup.downloads';
const DOWNLOADS_KEY = ['backup', 'downloads'] as const;
const KIND_LABELS: Record<string, string> = { excel: 'Arkusz Excel', full: 'Pełna kopia' };

type Download = { id: string; kind: string; userName: string; at: string };

const when = (iso: string) => {
  const { day, time } = warsawParts(iso);
  const [y, m, d] = day.split('-');
  return `${Number(d)}.${m}.${y}, ${time}`;
};

/**
 * "Eksport danych" (P11, owner remark 39 + answer 7): the spreadsheet to read and the full copy to restore, both for
 * the owner only (the server refuses everyone else). Plain links: the browser downloads the streamed file itself.
 * Below them who downloaded what and when (shown to whoever may see `backup.downloads`).
 */
export function ExportSettings({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const canSeeHistory = can(me, DOWNLOADS_RESOURCE, 'view');
  const history = useQuery({
    queryKey: DOWNLOADS_KEY,
    queryFn: () => api<{ downloads: Download[] }>('/api/backup/downloads'),
    enabled: canSeeHistory,
  });
  // the download is recorded on the server once it starts; show it in the history a moment later
  const refreshSoon = () => window.setTimeout(() => void queryClient.invalidateQueries({ queryKey: DOWNLOADS_KEY }), 2500);
  const downloads = history.data?.downloads ?? [];
  return (
    <section className="settings-section" aria-label="Eksport danych" data-feature-id="backup.export">
      <h2>Eksport danych</h2>
      <p className="hint">Wszystkie dane firmy do pliku — na wypadek awarii albo do przejrzenia w Excelu. Pliki zawierają też ceny i stawki: przechowuj je bezpiecznie.</p>
      {me.isOwner && (
        <div className="export-actions">
          <div className="export-action">
            <a className="btn" href="/api/backup/excel" download onClick={refreshSoon}>
              Pobierz arkusz Excel
            </a>
            <span className="hint">Do przeglądania: katalog, egzemplarze, wydarzenia, kosztorysy, sprzętówki, kontrahenci, osoby.</span>
          </div>
          <div className="export-action">
            <a className="btn" href="/api/backup/full" download onClick={refreshSoon}>
              Pobierz pełną kopię
            </a>
            <span className="hint">Cała baza i wgrane pliki (.tar.gz) — do odtworzenia programu po awarii. Zawiera też zahashowane hasła i aktywne linki kalendarzy: przechowuj ją jak hasło. Instrukcja w pliku ODTWORZENIE.txt w archiwum.</span>
          </div>
        </div>
      )}
      {canSeeHistory && (
        <>
          <h3 className="export-history-title">Historia pobrań</h3>
          {downloads.length === 0 ? (
            <p className="muted">{history.isLoading ? 'Wczytywanie…' : 'Nikt jeszcze nie pobierał danych.'}</p>
          ) : (
            <ul className="settings-list" aria-label="Historia pobrań">
              {downloads.map((d) => (
                <li key={d.id} className="settings-row">
                  <span className="settings-row-main">
                    <span>{KIND_LABELS[d.kind] ?? d.kind}</span>
                    <span className="muted">
                      {d.userName} · {when(d.at)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
